import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { communitiesAPI } from '../lib/api';
import { Search, Users, ExternalLink, Filter, X } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

export const CommunitiesPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [communities, setCommunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [category, setCategory] = useState(searchParams.get('category') || 'all');

  const categories = [
    { value: 'all', label: 'All Categories' },
    { value: 'ai-ml', label: 'AI & Machine Learning' },
    { value: 'robotics', label: 'Robotics & Hardware' },
    { value: 'creative', label: 'Creative Tech' },
    { value: 'startup', label: 'Startup Networks' },
    { value: 'diversity', label: 'Diversity & Inclusion' },
  ];

  useEffect(() => {
    const fetchCommunities = async () => {
      setLoading(true);
      try {
        const params = {};
        if (search) params.search = search;
        if (category && category !== 'all') params.category = category;
        const res = await communitiesAPI.getAll(params);
        setCommunities(res.data);
      } catch (err) {
        console.error('Error fetching communities:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchCommunities();
  }, [search, category]);

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (category && category !== 'all') params.set('category', category);
    setSearchParams(params);
  };

  const clearFilters = () => {
    setSearch('');
    setCategory('all');
    setSearchParams({});
  };

  const hasFilters = search || (category && category !== 'all');

  return (
    <div data-testid="communities-page">
      {/* Header */}
      <section className="border-b border-border">
        <div className="container-wide py-12">
          <span className="text-label text-muted-foreground mb-4 block">Directory</span>
          <h1 className="text-display text-4xl lg:text-5xl mb-4">Tech Communities</h1>
          <p className="text-muted-foreground max-w-2xl">
            Discover and connect with Portland's vibrant tech community groups. 
            From AI and robotics to creative technology and startup networks.
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="border-b border-border sticky top-16 z-40 bg-background">
        <div className="container-wide py-4">
          <form onSubmit={handleSearch} className="flex flex-wrap gap-3 items-center">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search communities..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="communities-search"
              />
            </div>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="w-[200px]" data-testid="category-select">
                <Filter className="w-4 h-4 mr-2 text-muted-foreground" />
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                {categories.map(cat => (
                  <SelectItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button type="submit" data-testid="search-btn">Search</Button>
            {hasFilters && (
              <Button type="button" variant="ghost" onClick={clearFilters} data-testid="clear-filters-btn">
                <X className="w-4 h-4 mr-1" />
                Clear
              </Button>
            )}
          </form>
        </div>
      </section>

      {/* Results */}
      <section>
        <div className="container-wide py-12">
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="border border-border p-6 animate-pulse">
                  <div className="w-12 h-12 bg-muted rounded-sm mb-4" />
                  <div className="h-4 bg-muted rounded w-3/4 mb-2" />
                  <div className="h-3 bg-muted rounded w-1/2" />
                </div>
              ))}
            </div>
          ) : communities.length > 0 ? (
            <>
              <p className="text-sm text-muted-foreground mb-6">
                {communities.length} {communities.length === 1 ? 'community' : 'communities'} found
              </p>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {communities.map((community, index) => (
                  <Link 
                    key={community.id}
                    to={`/communities/${community.id}`}
                    className="group animate-slide-up"
                    style={{ animationDelay: `${index * 50}ms` }}
                    data-testid={`community-card-${community.id}`}
                  >
                    <Card className="card-hover h-full">
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div className="w-12 h-12 rounded-sm bg-muted flex items-center justify-center">
                            {community.image_url ? (
                              <img 
                                src={community.image_url} 
                                alt={community.name}
                                className="w-full h-full object-cover rounded-sm grayscale-hover"
                              />
                            ) : (
                              <Users className="w-6 h-6 text-muted-foreground" />
                            )}
                          </div>
                          {community.featured && (
                            <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full font-mono">
                              Featured
                            </span>
                          )}
                        </div>
                        <span className="badge-category mb-2 inline-block">
                          {community.category}
                        </span>
                        <h3 className="font-semibold text-lg mb-2 group-hover:text-primary transition-colors">
                          {community.name}
                        </h3>
                        <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
                          {community.description}
                        </p>
                        <div className="flex items-center justify-between text-sm text-muted-foreground">
                          {community.member_count && (
                            <span>{community.member_count.toLocaleString()} members</span>
                          )}
                          {community.meetup_frequency && (
                            <span>{community.meetup_frequency}</span>
                          )}
                        </div>
                        {community.website && (
                          <div className="mt-4 pt-4 border-t border-border">
                            <span className="text-xs text-muted-foreground flex items-center gap-1">
                              <ExternalLink className="w-3 h-3" />
                              Visit website
                            </span>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            </>
          ) : (
            <div className="empty-state">
              <Users className="w-12 h-12 text-muted-foreground mb-4" />
              <h3 className="font-semibold text-lg mb-2">No communities found</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Try adjusting your search or filters
              </p>
              <Button variant="outline" onClick={clearFilters}>
                Clear all filters
              </Button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};
